import { apiClient } from "./apiClient";

import type {
  AdminProductDetailsData,
  AdminProductsListData,
  AdminProductsQuery,
  BulkActivateProductsInput,
  BulkAdjustProductStockInput,
  BulkDeactivateProductsInput,
  BulkDeleteProductsInput,
  BulkRestoreProductsInput,
  BulkSetProductStockInput,
  BulkUpdateProductCategoriesInput,
  BulkUpdateProductPricesInput,
  BulkUpdateProductTagsInput,
  ProductBulkOperationData,
  AdjustProductStockInput,
  CreateProductInput,
  DeleteProductData,
  DeleteProductSubImageData,
  ProductAgentPricesData,
  ProductDetailsData,
  ProductImageData,
  ProductImportBackupDetailsData,
  ProductImportBackupDetailsQuery,
  ProductImportBackupsData,
  ProductImportData,
  ProductImportHistoryQuery,
  ProductImportOptions,
  ProductImportRestoreData,
  ProductImportRunDetailsData,
  ProductImportRunDetailsQuery,
  ProductImportRunsData,
  ProductItemData,
  ProductPricesData,
  ProductsListData,
  ProductsQuery,
  ProductStockData,
  RestoreProductData,
  SetProductStockInput,
  UpdateAgentPricesInput,
  UpdateProductData,
  UpdateProductInput,
  UpdateProductPricesInput,
  InventoryTransactionsQuery,
  ProductInventoryTransactionsData,
  ProductAuditLogsPageData,
 ProductAuditLogsQuery,
} from "../types/products.types";

/* =========================================================
 * Helpers
 * ======================================================= */

function setStringParam(
  params: URLSearchParams,
  key: string,
  value: string | undefined,
) {
  if (!value?.trim()) {
    return;
  }

  params.set(
    key,
    value.trim(),
  );
}


function setArrayParam(
  params: URLSearchParams,
  key: string,
  value:
    | string
    | string[]
    | undefined,
) {
  if (!value) {
    return;
  }

  if (Array.isArray(value)) {
    const values = value
      .map((item) => item.trim())
      .filter(Boolean);

    if (values.length === 0) {
      return;
    }

    params.set(
      key,
      values.join(","),
    );

    return;
  }

  if (value.trim()) {
    params.set(
      key,
      value.trim(),
    );
  }
}

function setBooleanParam(
  params: URLSearchParams,
  key: string,
  value: boolean | undefined,
) {
  if (value === undefined) {
    return;
  }

  params.set(
    key,
    String(value),
  );
}

/* =========================================================
 * Products List Query
 * ======================================================= */

function buildProductsQuery(
  query: ProductsQuery = {},
): string {
  const params =
    new URLSearchParams();


  /* =========================
   * Tag
   * ======================= */

  setStringParam(
    params,
    "tagId",
    query.tagId,
  );

  setStringParam(
    params,
    "tagName",
    query.tagName,
  );


  /* =========================
   * Search
   * ======================= */

  setStringParam(
    params,
    "search",
    query.search,
  );

  setStringParam(
    params,
    "company",
    query.company,
  );

  setStringParam(
    params,
    "type",
    query.type,
  );


  /* =========================
   * Brand
   * ======================= */

  setStringParam(
    params,
    "brandId",
    query.brandId,
  );

  setStringParam(
    params,
    "brandName",
    query.brandName,
  );

  setArrayParam(
    params,
    "brands",
    query.brands,
  );

  setArrayParam(
    params,
    "brandIds",
    query.brandIds,
  );


  /* =========================
   * Categories
   * ======================= */

  setStringParam(
    params,
    "mainCategoryId",
    query.mainCategoryId,
  );

  setStringParam(
    params,
    "subCategoryId",
    query.subCategoryId,
  );

  setStringParam(
    params,
    "leafCategoryId",
    query.leafCategoryId,
  );

  setStringParam(
    params,
    "categoryPathKey",
    query.categoryPathKey,
  );

  setStringParam(
    params,
    "categoryPathId",
    query.categoryPathId,
  );

  setArrayParam(
    params,
    "mainCategoryIds",
    query.mainCategoryIds,
  );

  setArrayParam(
    params,
    "subCategoryIds",
    query.subCategoryIds,
  );

  setArrayParam(
    params,
    "leafCategoryIds",
    query.leafCategoryIds,
  );

  setArrayParam(
    params,
    "categoryPathIds",
    query.categoryPathIds,
  );

  setArrayParam(
    params,
    "categoryPathKeys",
    query.categoryPathKeys,
  );


  /* =========================
   * Tags
   * ======================= */

  setArrayParam(
    params,
    "tags",
    query.tags,
  );


  /* =========================
   * Legacy Camera Filters
   * ======================= */

  setArrayParam(
    params,
    "cameraTypes",
    query.cameraTypes,
  );

  setArrayParam(
    params,
    "lensTypes",
    query.lensTypes,
  );

  setArrayParam(
    params,
    "cameraTypeIds",
    query.cameraTypeIds,
  );

  setArrayParam(
    params,
    "lensTypeIds",
    query.lensTypeIds,
  );


  /* =========================
   * Dynamic Attributes
   * ======================= */

  setArrayParam(
    params,
    "attribute_camera_type",
    query.attribute_camera_type,
  );

  setArrayParam(
    params,
    "attribute_lens_type",
    query.attribute_lens_type,
  );

  setArrayParam(
    params,
    "attribute_resolution",
    query.attribute_resolution,
  );

  setArrayParam(
    params,
    "attribute_channels",
    query.attribute_channels,
  );

  setArrayParam(
    params,
    "attribute_recorder_type",
    query.attribute_recorder_type,
  );

  setArrayParam(
    params,
    "attribute_storage_type",
    query.attribute_storage_type,
  );

  setArrayParam(
    params,
    "attribute_power_type",
    query.attribute_power_type,
  );


  /* =========================
   * Prices
   * ======================= */

  if (
    query.minPrice !== undefined
  ) {
    params.set(
      "minPrice",
      String(query.minPrice),
    );
  }

  if (
    query.maxPrice !== undefined
  ) {
    params.set(
      "maxPrice",
      String(query.maxPrice),
    );
  }


  /* =========================
   * Stock
   * ======================= */

  if (query.stockStatus) {
    params.set(
      "stockStatus",
      query.stockStatus,
    );
  }


  /* =========================
   * Sorting
   * ======================= */

  if (query.sort) {
    params.set(
      "sort",
      query.sort,
    );
  }


  /* =========================
   * Pagination
   * ======================= */

  setStringParam(
    params,
    "cursor",
    query.cursor,
  );

  if (
    query.limit !== undefined
  ) {
    params.set(
      "limit",
      String(query.limit),
    );
  }


  const queryString =
    params.toString();

  return queryString
    ? `?${queryString}`
    : "";
}


function buildAdminProductsQuery(
  query: AdminProductsQuery = {},
): string {
  const params =
    new URLSearchParams();


  /* =========================
   * Search
   * ======================= */

  setStringParam(
    params,
    "search",
    query.search,
  );

  setStringParam(
    params,
    "code",
    query.code,
  );

  setStringParam(
    params,
    "name",
    query.name,
  );

  setStringParam(
    params,
    "type",
    query.type,
  );


  /* =========================
   * Brand
   * ======================= */

  setStringParam(
    params,
    "brandId",
    query.brandId,
  );

  setStringParam(
    params,
    "brandName",
    query.brandName,
  );

  setArrayParam(
    params,
    "brandIds",
    query.brandIds,
  );

  setArrayParam(
    params,
    "brands",
    query.brands,
  );


  /* =========================
   * Categories
   * ======================= */

  setStringParam(
    params,
    "mainCategoryId",
    query.mainCategoryId,
  );

  setStringParam(
    params,
    "subCategoryId",
    query.subCategoryId,
  );

  setStringParam(
    params,
    "leafCategoryId",
    query.leafCategoryId,
  );

  setStringParam(
    params,
    "categoryPathId",
    query.categoryPathId,
  );

  setStringParam(
    params,
    "categoryPathKey",
    query.categoryPathKey,
  );

  setArrayParam(
    params,
    "mainCategoryIds",
    query.mainCategoryIds,
  );

  setArrayParam(
    params,
    "subCategoryIds",
    query.subCategoryIds,
  );

  setArrayParam(
    params,
    "leafCategoryIds",
    query.leafCategoryIds,
  );

  setArrayParam(
    params,
    "categoryPathIds",
    query.categoryPathIds,
  );

  setArrayParam(
    params,
    "categoryPathKeys",
    query.categoryPathKeys,
  );


  /* =========================
   * Tags
   * ======================= */

  setArrayParam(
    params,
    "tags",
    query.tags,
  );


  /* =========================
   * Legacy Filters
   * ======================= */

  setArrayParam(
    params,
    "cameraTypes",
    query.cameraTypes,
  );

  setArrayParam(
    params,
    "lensTypes",
    query.lensTypes,
  );

  setArrayParam(
    params,
    "cameraTypeIds",
    query.cameraTypeIds,
  );

  setArrayParam(
    params,
    "lensTypeIds",
    query.lensTypeIds,
  );


  /* =========================
   * Attributes
   * ======================= */

  setArrayParam(
    params,
    "attribute_camera_type",
    query.attribute_camera_type,
  );

  setArrayParam(
    params,
    "attribute_lens_type",
    query.attribute_lens_type,
  );

  setArrayParam(
    params,
    "attribute_resolution",
    query.attribute_resolution,
  );

  setArrayParam(
    params,
    "attribute_channels",
    query.attribute_channels,
  );

  setArrayParam(
    params,
    "attribute_recorder_type",
    query.attribute_recorder_type,
  );

  setArrayParam(
    params,
    "attribute_storage_type",
    query.attribute_storage_type,
  );

  setArrayParam(
    params,
    "attribute_power_type",
    query.attribute_power_type,
  );


  /* =========================
   * Price
   * ======================= */

  if (
    query.minPrice !== undefined
  ) {
    params.set(
      "minPrice",
      String(query.minPrice),
    );
  }

  if (
    query.maxPrice !== undefined
  ) {
    params.set(
      "maxPrice",
      String(query.maxPrice),
    );
  }


  /* =========================
   * Stock
   * ======================= */

  if (query.stockStatus) {
    params.set(
      "stockStatus",
      query.stockStatus,
    );
  }


  /* =========================
   * Administrative State
   * ======================= */

  setBooleanParam(
    params,
    "isActive",
    query.isActive,
  );

  setBooleanParam(
    params,
    "isDeleted",
    query.isDeleted,
  );

  setBooleanParam(
    params,
    "showStatus",
    query.showStatus,
  );

  setBooleanParam(
    params,
    "searchEnabled",
    query.searchEnabled,
  );


  /* =========================
   * Sorting
   * ======================= */

  if (query.sort) {
    params.set(
      "sort",
      query.sort,
    );
  }


  /* =========================
   * Pagination
   * ======================= */

  setStringParam(
    params,
    "cursor",
    query.cursor,
  );

  if (
    query.limit !== undefined
  ) {
    params.set(
      "limit",
      String(query.limit),
    );
  }


  const queryString =
    params.toString();

  return queryString
    ? `?${queryString}`
    : "";
}

/* =========================================================
 * Import Query
 * ======================================================= */

function buildImportQuery(
  options: ProductImportOptions = {},
): string {
  const params =
    new URLSearchParams();

  if (
    options.dryRun !== undefined
  ) {
    params.set(
      "dryRun",
      String(options.dryRun),
    );
  }

  if (options.mode) {
    params.set(
      "mode",
      options.mode,
    );
  }

  if (options.fields) {
    const fields =
      Array.isArray(options.fields)
        ? options.fields.join(",")
        : options.fields;

    params.set(
      "fields",
      fields,
    );
  }

  if (
    options.allowDeletedUpdate !==
    undefined
  ) {
    params.set(
      "allowDeletedUpdate",
      String(
        options.allowDeletedUpdate,
      ),
    );
  }

  if (
    options.backup !== undefined
  ) {
    params.set(
      "backup",
      String(options.backup),
    );
  }

  const queryString =
    params.toString();

  return queryString
    ? `?${queryString}`
    : "";
}


/* =========================================================
 * History Query
 * ======================================================= */

function buildHistoryQuery(
  query:
    ProductImportHistoryQuery = {},
): string {
  const params =
    new URLSearchParams();

  if (
    query.limit !== undefined
  ) {
    params.set(
      "limit",
      String(query.limit),
    );
  }

  setStringParam(
    params,
    "cursor",
    query.cursor,
  );

  const queryString =
    params.toString();

  return queryString
    ? `?${queryString}`
    : "";
}

/* =========================================================
 * Inventory Transactions Query
 * ======================================================= */

function buildInventoryTransactionsQuery(
  query:
    InventoryTransactionsQuery = {},
): string {
  const params =
    new URLSearchParams();


  if (query.type) {
    params.set(
      "type",
      query.type,
    );
  }


  if (query.source) {
    params.set(
      "source",
      query.source,
    );
  }


  setStringParam(
    params,
    "orderId",
    query.orderId,
  );


  setStringParam(
    params,
    "reservationId",
    query.reservationId,
  );


  setStringParam(
    params,
    "performedBy",
    query.performedBy,
  );


  setStringParam(
    params,
    "dateFrom",
    query.dateFrom,
  );


  setStringParam(
    params,
    "dateTo",
    query.dateTo,
  );


  setStringParam(
    params,
    "cursor",
    query.cursor,
  );


  if (
    query.limit !== undefined
  ) {
    params.set(
      "limit",
      String(
        query.limit,
      ),
    );
  }


  const queryString =
    params.toString();


  return queryString
    ? `?${queryString}`
    : "";
}

/* =========================================================
 * Product Audit Query
 * ======================================================= */

function buildProductAuditQuery(
  query:
    ProductAuditLogsQuery = {},
): string {
  const params =
    new URLSearchParams();


  if (query.action) {
    params.set(
      "action",
      query.action,
    );
  }


  if (query.source) {
    params.set(
      "source",
      query.source,
    );
  }


  setStringParam(
    params,
    "performedBy",
    query.performedBy,
  );


  setStringParam(
    params,
    "performedByEmail",
    query.performedByEmail,
  );


  setStringParam(
    params,
    "executionId",
    query.executionId,
  );


  setStringParam(
    params,
    "dateFrom",
    query.dateFrom,
  );


  setStringParam(
    params,
    "dateTo",
    query.dateTo,
  );


  if (query.sort) {
    params.set(
      "sort",
      query.sort,
    );
  }


  setStringParam(
    params,
    "cursor",
    query.cursor,
  );


  if (
    query.limit !== undefined
  ) {
    params.set(
      "limit",
      String(
        query.limit,
      ),
    );
  }


  const queryString =
    params.toString();


  return queryString
    ? `?${queryString}`
    : "";
}


/* =========================================================
 * Products API
 * ======================================================= */

export const productsApi = {

      /* =======================================================
   * Dashboard / Admin Products
   * ===================================================== */

  adminList(
    query: AdminProductsQuery = {},
  ) {
    return apiClient.get<AdminProductsListData>(
      `/products/admin${buildAdminProductsQuery(
        query,
      )}`,
    );
  },


  adminGetById(
    productId: string,
  ) {
    return apiClient.get<AdminProductDetailsData>(
      `/products/admin/${encodeURIComponent(
        productId,
      )}`,
    );
  },


  /* =======================================================
 * Product Excel Files
 * ===================================================== */

downloadImportTemplate() {
  return apiClient.getBlob(
    "/products/import/template",
  );
},


exportProducts() {
  return apiClient.getBlob(
    "/products/export",
  );
},
    /* =======================================================
   * Bulk Product Operations
   * ===================================================== */


  /* =========================
   * Activate
   * ======================= */

  bulkActivate(
    input: BulkActivateProductsInput,
  ) {
    return apiClient.post<ProductBulkOperationData>(
      "/products/bulk/activate",
      input,
    );
  },


  /* =========================
   * Deactivate
   * ======================= */

  bulkDeactivate(
    input: BulkDeactivateProductsInput,
  ) {
    return apiClient.post<ProductBulkOperationData>(
      "/products/bulk/deactivate",
      input,
    );
  },


  /* =========================
   * Delete
   * ======================= */

  bulkDelete(
    input: BulkDeleteProductsInput,
  ) {
    return apiClient.post<ProductBulkOperationData>(
      "/products/bulk/delete",
      input,
    );
  },


  /* =========================
   * Restore
   * ======================= */

  bulkRestore(
    input: BulkRestoreProductsInput,
  ) {
    return apiClient.post<ProductBulkOperationData>(
      "/products/bulk/restore",
      input,
    );
  },


  /* =========================
   * Stock - Set
   * ======================= */

  bulkSetStock(
    input: BulkSetProductStockInput,
  ) {
    return apiClient.patch<ProductBulkOperationData>(
      "/products/bulk/stock",
      input,
    );
  },


  /* =========================
   * Stock - Adjust
   * ======================= */

  bulkAdjustStock(
    input: BulkAdjustProductStockInput,
  ) {
    return apiClient.patch<ProductBulkOperationData>(
      "/products/bulk/stock",
      input,
    );
  },


  /* =========================
   * Prices
   * ======================= */

  bulkUpdatePrices(
    input: BulkUpdateProductPricesInput,
  ) {
    return apiClient.patch<ProductBulkOperationData>(
      "/products/bulk/prices",
      input,
    );
  },


  /* =========================
   * Tags
   * ======================= */

  bulkUpdateTags(
    input: BulkUpdateProductTagsInput,
  ) {
    return apiClient.patch<ProductBulkOperationData>(
      "/products/bulk/tags",
      input,
    );
  },


  /* =========================
   * Categories / Brands
   * ======================= */

  bulkUpdateCategories(
    input: BulkUpdateProductCategoriesInput,
  ) {
    return apiClient.patch<ProductBulkOperationData>(
      "/products/bulk/categories",
      input,
    );
  },

  /* =======================================================
 * Product Audit Logs
 * ===================================================== */

getAuditLogs(
  productId: string,

  query:
    ProductAuditLogsQuery = {},
) {
  return apiClient.get<ProductAuditLogsPageData>(
    `/products/${encodeURIComponent(
      productId,
    )}/audit-logs${buildProductAuditQuery(
      query,
    )}`,
  );
},

  /* =======================================================
   * Products
   * ===================================================== */


  list(
    query: ProductsQuery = {},
  ) {
    return apiClient.get<ProductsListData>(
      `/products${buildProductsQuery(
        query,
      )}`,
    );
  },


  getById(
    productId: string,
  ) {
    return apiClient.get<ProductDetailsData>(
      `/products/${encodeURIComponent(
        productId,
      )}`,
    );
  },


  create(
    input: CreateProductInput,
  ) {
    return apiClient.post<ProductItemData>(
      "/products",
      input,
    );
  },


  update(
    productId: string,
    input: UpdateProductInput,
  ) {
    return apiClient.patch<UpdateProductData>(
      `/products/${encodeURIComponent(
        productId,
      )}`,
      input,
    );
  },


  delete(
    productId: string,
  ) {
    return apiClient.delete<DeleteProductData>(
      `/products/${encodeURIComponent(
        productId,
      )}`,
    );
  },


  restore(
    productId: string,
  ) {
    return apiClient.post<RestoreProductData>(
      `/products/${encodeURIComponent(
        productId,
      )}/restore`,
    );
  },


  /* =======================================================
   * Stock
   * ===================================================== */

  setStock(
    productId: string,
    input: SetProductStockInput,
  ) {
    return apiClient.patch<ProductStockData>(
      `/products/${encodeURIComponent(
        productId,
      )}/stock`,
      input,
    );
  },


  adjustStock(
    productId: string,
    input: AdjustProductStockInput,
  ) {
    return apiClient.post<ProductStockData>(
      `/products/${encodeURIComponent(
        productId,
      )}/stock/adjust`,
      input,
    );
  },

  /* =======================================================
 * Inventory Transactions
 * ===================================================== */

getInventoryTransactions(
  productId: string,

  query:
    InventoryTransactionsQuery = {},
) {
  return apiClient.get<ProductInventoryTransactionsData>(
    `/products/${encodeURIComponent(
      productId,
    )}/inventory-transactions${buildInventoryTransactionsQuery(
      query,
    )}`,
  );
},


  /* =======================================================
   * Prices
   * ===================================================== */

  updatePrices(
    productId: string,
    input: UpdateProductPricesInput,
  ) {
    return apiClient.patch<ProductPricesData>(
      `/products/${encodeURIComponent(
        productId,
      )}/prices`,
      input,
    );
  },


  updateAgentPrices(
    productId: string,
    input: UpdateAgentPricesInput,
  ) {
    return apiClient.patch<ProductAgentPricesData>(
      `/products/${encodeURIComponent(
        productId,
      )}/agent-prices`,
      input,
    );
  },


  /* =======================================================
   * Main Image
   * ===================================================== */

  uploadMainImage(
    productId: string,
    image: File,
  ) {
    const formData =
      new FormData();

    formData.append(
      "image",
      image,
    );

    return apiClient.post<ProductImageData>(
      `/products/${encodeURIComponent(
        productId,
      )}/images/main`,
      formData,
    );
  },


  /* =======================================================
   * Sub Images
   * ===================================================== */

  uploadSubImage(
    productId: string,
    image: File,
  ) {
    const formData =
      new FormData();

    formData.append(
      "image",
      image,
    );

    return apiClient.post<ProductImageData>(
      `/products/${encodeURIComponent(
        productId,
      )}/images/sub`,
      formData,
    );
  },


  deleteSubImage(
    productId: string,
    imageId: string,
  ) {
    return apiClient.delete<DeleteProductSubImageData>(
      `/products/${encodeURIComponent(
        productId,
      )}/images/sub/${encodeURIComponent(
        imageId,
      )}`,
    );
  },


  /* =======================================================
   * Excel Import
   * ===================================================== */

  importProducts(
    file: File,
    options:
      ProductImportOptions = {},
  ) {
    const formData =
      new FormData();

    formData.append(
      "file",
      file,
    );

    return apiClient.post<ProductImportData>(
      `/products/import${buildImportQuery(
        options,
      )}`,
      formData,
    );
  },


  /* =======================================================
   * Import Backups
   * ===================================================== */

  getImportBackups(
    query:
      ProductImportHistoryQuery = {},
  ) {
    return apiClient.get<ProductImportBackupsData>(
      `/products/import/backups${buildHistoryQuery(
        query,
      )}`,
    );
  },


  getImportBackupById(
    backupId: string,
    query:
      ProductImportBackupDetailsQuery = {},
  ) {
    const params =
      new URLSearchParams();

    if (
      query.includeData !== undefined
    ) {
      params.set(
        "includeData",
        String(
          query.includeData,
        ),
      );
    }

    const queryString =
      params.toString();

    return apiClient.get<ProductImportBackupDetailsData>(
      `/products/import/backups/${encodeURIComponent(
        backupId,
      )}${
        queryString
          ? `?${queryString}`
          : ""
      }`,
    );
  },


  restoreImportBackup(
    backupId: string,
  ) {
    return apiClient.post<ProductImportRestoreData>(
      `/products/import/backups/${encodeURIComponent(
        backupId,
      )}/restore`,
    );
  },


  /* =======================================================
   * Import Runs
   * ===================================================== */

  getImportRuns(
    query:
      ProductImportHistoryQuery = {},
  ) {
    return apiClient.get<ProductImportRunsData>(
      `/products/import/runs${buildHistoryQuery(
        query,
      )}`,
    );
  },


  getImportRunById(
    importId: string,
    query:
      ProductImportRunDetailsQuery = {},
  ) {
    const params =
      new URLSearchParams();

    if (
      query.includeItems !== undefined
    ) {
      params.set(
        "includeItems",
        String(
          query.includeItems,
        ),
      );
    }

    const queryString =
      params.toString();

    return apiClient.get<ProductImportRunDetailsData>(
      `/products/import/runs/${encodeURIComponent(
        importId,
      )}${
        queryString
          ? `?${queryString}`
          : ""
      }`,
    );
  },
};